import L from 'leaflet';

// Shows the regular (coloured) map inside one hovered area, on top of the white map, by clipping a
// second tile layer to the area's outline. Shared by kartvy (Map.js) and listvy (ListViewPage.js).
// The clip is in layer pixels, the same space the map's layers are drawn in, so panning needs no
// update; zooming does (animateClipForZoom on zoomanim, applyClip on zoomend/viewreset).
// The hover map can also be an OpenFreeMap (MapLibre) layer (openFreeMap.js): its canvas sits at its own
// offset and moves while panning, so the clip is measured from that offset and re-applied on its
// 'reposition' event; during a zoom animation its clip animates like a tile layer's.

// Clip that hides the whole hover map layer.
const HIDDEN_CLIP_PATH = 'polygon(0 0, 0 0, 0 0)';
// Bounds that match no tiles, so the hover map loads nothing until an area is hovered.
export const EMPTY_TILE_BOUNDS = [[0, 0], [0, 0]];
// Fade of the regular map in/out.
const REVEAL_FADE_MS = 240;
// Leaflet's own zoom animation timing (.leaflet-zoom-anim in leaflet.css), so the clip moves with the tiles.
const LEAFLET_ZOOM_TRANSITION = '0.25s cubic-bezier(0,0,0.25,1)';
const REVEAL_OPACITY_TRANSITION = `opacity ${REVEAL_FADE_MS}ms ease-out`;

// CSS clip for one area's outline; toPoint maps a LatLng to layer pixels.
const buildRevealClipPath = (featureLayer, toPoint) => {
    const rings = [];
    const collectRings = (value) => {
        if (L.LineUtil.isFlat(value)) {
            rings.push(value);
        } else {
            value.forEach(collectRings);
        }
    };
    collectRings(featureLayer.getLatLngs());
    const pathData = rings.map((ring) => ring.map((latLng, index) => {
        const point = toPoint(latLng);
        return `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)},${point.y.toFixed(1)}`;
    }).join('') + 'Z').join('');
    return `path(evenodd, '${pathData}')`;
};

const isGlLayer = (layer) => typeof layer?.getMaplibreMap === 'function';

// getTileLayer returns the hover layer, a TileLayer or an OpenFreeMap layer (or null while none is mounted).
export const createAreaMapReveal = (getTileLayer) => {
    let revealedLayer = null;
    let fadeTimer = null;

    const getContainer = () => getTileLayer()?.getContainer?.() || null;

    const applyClip = () => {
        const tileLayer = getTileLayer();
        const container = getContainer();
        if (!container) return;
        const currentMap = tileLayer._map;
        // A tile layer's container sits at the layer origin; a MapLibre canvas at its own offset.
        const origin = L.DomUtil.getPosition(container) || L.point(0, 0);
        container.style.transition = REVEAL_OPACITY_TRANSITION;
        container.style.clipPath = revealedLayer && currentMap
            ? buildRevealClipPath(revealedLayer, (latLng) => currentMap.latLngToLayerPoint(latLng).subtract(origin))
            : HIDDEN_CLIP_PATH;
    };

    // During Leaflet's zoom animation the tiles (or the MapLibre canvas, inside its container that stays
    // put) scale with a CSS transition. Moving the clip to where the outline ends up, with the same timing,
    // keeps the regular map on the area for the whole zoom, instead of hiding it until the zoom is done.
    const animateClipForZoom = (event) => {
        const tileLayer = getTileLayer();
        const currentMap = tileLayer?._map;
        const container = getContainer();
        if (!container || !revealedLayer || !currentMap) return;
        const origin = isGlLayer(tileLayer) ? (L.DomUtil.getPosition(container) || L.point(0, 0)) : L.point(0, 0);
        container.style.transition = `clip-path ${LEAFLET_ZOOM_TRANSITION}, ${REVEAL_OPACITY_TRANSITION}`;
        // Private Leaflet API (stable in 1.9): the layer point a LatLng will have after the zoom.
        container.style.clipPath = buildRevealClipPath(
            revealedLayer,
            (latLng) => currentMap._latLngToNewLayerPoint(latLng, event.zoom, event.center).subtract(origin),
        );
    };

    const fadeOpacity = (container, opacity, fromZero = false) => {
        if (fromZero) {
            container.style.transition = 'none';
            container.style.opacity = '0';
            // Apply the starting opacity before the transition is switched back on.
            container.getBoundingClientRect();
        }
        container.style.transition = REVEAL_OPACITY_TRANSITION;
        container.style.opacity = String(opacity);
    };

    const reveal = (featureLayer) => {
        const tileLayer = getTileLayer();
        const container = getContainer();
        if (!container) return;
        // Restyles of the hovered area call this again; keep the reveal as it is.
        if (revealedLayer === featureLayer) return;
        clearTimeout(fadeTimer);
        revealedLayer = featureLayer;
        if (tileLayer instanceof L.TileLayer) {
            // Only load tiles that overlap the hovered area (private Leaflet API, stable in 1.9).
            L.Util.setOptions(tileLayer, { bounds: featureLayer.getBounds().pad(0.05) });
            if (tileLayer._map) tileLayer._update();
        }
        applyClip();
        fadeOpacity(container, 1, true);
    };

    // featureLayer: only hide if that area is the one shown.
    const hide = (featureLayer = null) => {
        if (featureLayer && revealedLayer !== featureLayer) return;
        if (!revealedLayer) return;
        revealedLayer = null;
        const container = getContainer();
        if (!container) return;
        // Fade out inside the old clip, then remove the clip unless another area was hovered meanwhile.
        fadeOpacity(container, 0);
        clearTimeout(fadeTimer);
        fadeTimer = setTimeout(() => {
            if (!revealedLayer) applyClip();
        }, REVEAL_FADE_MS);
    };

    // Hide at once, e.g. when the hover layer is (re)created or turned off.
    const reset = () => {
        clearTimeout(fadeTimer);
        revealedLayer = null;
        applyClip();
    };

    return { reveal, hide, applyClip, animateClipForZoom, reset };
};
